import { call, put, takeEvery } from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  uploadImageRequest,
  uploadImageSuccess,
  uploadImageFailure,
} from '../slices/imageUploadSlice';
import type { UploadImagePayload } from '../slices/imageUploadSlice';
import { IMAGE_UPLOAD_URL } from '../../api/config';

// Response type from API
interface ImageUploadResponse {
  image_url: string;
}

function* uploadImageSaga(
  action: PayloadAction<UploadImagePayload>
): Generator<any, void, any> {
  try {
    const {
      file,
      pexelsUrl,
      templateUrl,
    } = action.payload;

    let imageUrl: string;

    if (file) {
      const formData = new FormData();
      formData.append('image', file);

      const response: {
        data: ImageUploadResponse;
      } = yield call(
        axios.post,
        `${IMAGE_UPLOAD_URL}/imageUpload`,
        formData,
        {
          headers: {
            'Content-Type':
            'multipart/form-data',
          },
        }
      );

      imageUrl = response.data.image_url;
    }

    else if (pexelsUrl) {
      const imageResponse: Response =
        yield call(fetch, pexelsUrl);

      if (!imageResponse.ok) {
        throw new Error(
          `Failed to fetch Pexels image: ${imageResponse.status}`
        );
      }

      const imageBlob: Blob =
        yield call(
          [imageResponse, 'blob']
        );

      const fileName = `pexels-${Date.now()}.jpg`;

      const imageFile = new File(
        [imageBlob],
        fileName,
        {
          type:
            imageBlob.type ||
            'image/jpeg',
        }
      );

      const formData = new FormData();

      formData.append(
        'image',
        imageFile
      );

      const response: {
        data: ImageUploadResponse;
      } = yield call(
        axios.post,
        `${IMAGE_UPLOAD_URL}/imageUpload`,
        formData,
        {
          headers: {
            'Content-Type':
              'multipart/form-data',
          },
        }
      );

      imageUrl =
        response.data.image_url;
    }
    else if (templateUrl) {
      const response: {
        data: ImageUploadResponse;
      } = yield call(
        axios.post,
        `${IMAGE_UPLOAD_URL}/imageUpload`,
        {
          image_url: templateUrl,
        }
      );

      imageUrl =
        response.data.image_url;
    }
    else {
      throw new Error(
        'No file, Pexels URL, or template URL provided'
      );
    }

    if (!imageUrl) {
      throw new Error(
        'No image URL returned from server'
      );
    }

    yield put(
      uploadImageSuccess({
        imageUrl,
      })
    );
  } catch (error: any) {
    let errorMessage =
      'Failed to upload image';

    if (
      error.response?.data?.message
    ) {
      errorMessage =
        error.response.data.message;
    } else if (
      error.response?.data?.error
    ) {
      errorMessage =
        error.response.data.error;
    } else if (error.message) {
      errorMessage =
        error.message;
    }

    yield put(
      uploadImageFailure(
        errorMessage
      )
    );
  }
}

/**
 * Root saga for image upload
 */
export default function* imageUploadSagaWatcher() {
  yield takeEvery(
    uploadImageRequest.type,
    uploadImageSaga
  );
}
